import React, { useState, useMemo } from "react";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { analyzeComposition, buildTargetProfiles, analyzeTeamGap } from "../services/hiringGaps";
import { getArchetype } from "../data/archetypes";
import "../styles.css";

const TRAIT_FULL = { A: "Autonomy", B: "Social", C: "Pace", D: "Conformity" };

function CompositionCard({ c }) {
  return (
    <div className="comp-card">
      <div className="comp-head">
        <div className="comp-letter">
          <div className="comp-circle">{c.trait}</div>
          <span className="comp-name">{c.name}</span>
        </div>
        <span className={`comp-verdict ${c.verdictType}`}>{c.verdict}</span>
      </div>
      <div className="dist">
        {c.pct.low > 0 && <div className="dist-seg dist-low" style={{ width: `${c.pct.low}%` }}>{c.pct.low > 8 ? `${c.pct.low}%` : ""}</div>}
        {c.pct.mid > 0 && <div className="dist-seg dist-mid" style={{ width: `${c.pct.mid}%` }}>{c.pct.mid > 8 ? `${c.pct.mid}%` : ""}</div>}
        {c.pct.high > 0 && <div className="dist-seg dist-high" style={{ width: `${c.pct.high}%` }}>{c.pct.high > 8 ? `${c.pct.high}%` : ""}</div>}
      </div>
      <div className="dist-legend"><span>Low {c.trait}</span><span>Mid</span><span>High {c.trait}</span></div>
      <div className="comp-insight">{c.insight}</div>
    </div>
  );
}

function TargetProfileCard({ target }) {
  return (
    <div className="target">
      <div className="target-head">
        <div className="target-badge">
          <Icon name={target.icon} />
          {target.archetype && <span>{target.archetype}</span>}
        </div>
        <div>
          <div className="target-title">{target.title}</div>
          <div className="target-sub">Ideal for: {target.idealFor}</div>
        </div>
        <div className="target-priority">
          <div className="target-priority-num">Priority {target.priority}</div>
        </div>
      </div>
      <div className="target-body">
        {target.ranges && (
          <>
            <div className="target-section-label">Target Trait Ranges</div>
            {Object.entries(target.ranges).map(([t, [lo, hi]]) => (
              <div className="range-row" key={t}>
                <div className="range-label">{t} <small>{TRAIT_FULL[t]}</small></div>
                <div>
                  <div className="range-track">
                    <div className="range-band" style={{ left: `${(lo / 10) * 100}%`, width: `${((hi - lo) / 10) * 100}%` }}>{lo}–{hi}</div>
                  </div>
                  <div className="range-scale"><span>0</span><span>5</span><span>10</span></div>
                </div>
              </div>
            ))}
          </>
        )}
        <div className="target-why">
          <b>Why this profile:</b> {target.why}
          {target.altArchetypes && target.altArchetypes.length > 0 && (
            <div className="archetypes-hint">
              {target.archetype && <span className="arch-pill"><Icon name={target.icon} /> {target.archetype}</span>}
              {target.altArchetypes.map(a => (
                <span className="arch-pill" key={a.name}><Icon name={a.icon} /> {a.name}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TAB 1: ORG-WIDE GAPS ───────────────────────────────────
function OrgGaps({ people }) {
  const composition = useMemo(() => analyzeComposition(people), [people]);
  const targets = useMemo(() => buildTargetProfiles(composition), [composition]);

  return (
    <div>
      <div className="hr-intro">
        Behavioral composition across all <b>{people.length} profiled employees</b>. This shows where the workforce is thin or overweight on each trait — and what profiles to recruit to rebalance as you grow.
      </div>
      <div className="comp-grid">
        {composition.map(c => <CompositionCard key={c.trait} c={c} />)}
      </div>
      <div className="target-section-label" style={{ marginBottom: "14px" }}>Recruiting Targets — generated from the gaps above</div>
      {targets.map((t, i) => <div key={i} style={{ marginBottom: "16px" }}><TargetProfileCard target={t} /></div>)}
    </div>
  );
}

// ─── TAB 2: PER-TEAM GAPS ───────────────────────────────────
function TeamGaps({ people, projects, teamMembers }) {
  const [jobNumber, setJobNumber] = useState("");
  const activeJob = jobNumber || projects[0]?.Title || "";
  const project = projects.find(p => p.Title === activeJob);
  const members = teamMembers.filter(m => m.ProjectJobNumber === activeJob);
  const teamPeople = members.map(m => people.find(p => p.FullName === m.PersonName)).filter(Boolean);

  const analysis = useMemo(() => teamPeople.length ? analyzeTeamGap(teamPeople) : null, [teamPeople]);

  if (projects.length === 0) {
    return <div className="empty"><div className="empty-icon"><Icon name="users" /></div><div className="empty-text">No projects to analyze yet.</div></div>;
  }

  return (
    <div>
      <div className="hr-intro">
        Pick a project to see what behavioral roles its specific crew is missing.
      </div>
      <div className="form-group" style={{ maxWidth: "360px" }}>
        <label className="form-label">Project</label>
        <select className="form-select" value={activeJob} onChange={e => setJobNumber(e.target.value)}>
          {[...projects].sort((a, b) => (a.Title || "").localeCompare(b.Title || "")).map(p => <option key={p.Title} value={p.Title}>{p.Title} — {p.JobName}</option>)}
        </select>
      </div>

      {teamPeople.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="users" /></div>
          <div className="empty-text">No profiled team members on this project yet.<br />Add profiles to analyze the crew's gaps.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px" }}>
            Analyzing <b style={{ color: "var(--text-primary)" }}>{teamPeople.length}</b> profiled members of <b style={{ color: "var(--text-primary)" }}>{project.JobName}</b>
          </div>

          {/* Coverage checklist */}
          <div className="coverage-grid mb-24">
            {[
              { ok: analysis.hasDriver, label: "Driver", trait: "High A" },
              { ok: analysis.hasAnchor, label: "Steady Anchor", trait: "High C" },
              { ok: analysis.hasQuality, label: "Quality Control", trait: "High D" },
              { ok: analysis.hasGlue, label: "Communicator", trait: "High B" },
            ].map(item => (
              <div key={item.label} className={`coverage-item ${item.ok ? "ok" : "missing"}`}>
                <Icon name={item.ok ? "circle-check" : "x-circle"} size={22} />
                <div>
                  <div className="coverage-label">{item.label}</div>
                  <div className="coverage-trait">{item.trait} · {item.ok ? "covered" : "missing"}</div>
                </div>
              </div>
            ))}
          </div>

          {analysis.gaps.length === 0 ? (
            <div className="target" style={{ borderTopColor: "var(--green-bar)" }}>
              <div className="target-head">
                <div className="target-badge" style={{ background: "var(--green-soft)" }}>
                  <Icon name="circle-check" style={{ color: "var(--green)" }} />
                </div>
                <div>
                  <div className="target-title">Well-Rounded Crew</div>
                  <div className="target-sub">All four key behavioral roles are covered</div>
                </div>
              </div>
              <div className="target-body">
                <div className="target-why">This team has a driver, a steady anchor, quality control, and a communicator. No behavioral gaps to fill — staff additions can focus on workload capacity rather than personality balance.</div>
              </div>
            </div>
          ) : (
            <div>
              <div className="target-section-label" style={{ marginBottom: "14px" }}>What This Crew Needs</div>
              {analysis.gaps.map((g, i) => (
                <div key={i} className="gap-row">
                  <div className="comp-circle" style={{ background: "var(--red)" }}>{g.trait}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: "14px" }}>{g.label}</div>
                    <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{g.note}</div>
                  </div>
                  <div className="gap-need">Need High-{g.trait}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── TAB 3: BACKFILL ────────────────────────────────────────
function Backfill({ people, projects, teamMembers }) {
  const [personId, setPersonId] = useState("");
  const [mode, setMode] = useState("match"); // match | upgrade
  const person = people.find(p => String(p.Id) === String(personId));
  const arch = person ? getArchetype(person.Archetype) : null;

  // Find the person's current team (to compute upgrade target)
  const membership = person ? teamMembers.find(m => m.PersonName === person.FullName) : null;
  const teammates = membership
    ? teamMembers
        .filter(m => m.ProjectJobNumber === membership.ProjectJobNumber && m.PersonName !== person.FullName)
        .map(m => people.find(p => p.FullName === m.PersonName))
        .filter(Boolean)
    : [];
  const project = membership ? projects.find(p => p.Title === membership.ProjectJobNumber) : null;

  // Upgrade target: what does the REST of the team still need (without this person)?
  const teamGap = teammates.length >= 1 ? analyzeTeamGap(teammates) : null;

  // Build the upgrade trait ranges based on what the team lacks
  const upgradeRanges = useMemo(() => {
    if (!person) return null;
    const base = {};
    ["A", "B", "C", "D"].forEach(t => {
      const v = person[`Survey_${t}`];
      base[t] = [Math.max(0, v - 1), Math.min(10, v + 1)];
    });
    if (!teamGap) return base;
    // For each missing coverage, push that trait's target range high
    teamGap.gaps.forEach(g => {
      if (g.trait === "A") base.A = [6, 9];
      if (g.trait === "C") base.C = [6, 9];
      if (g.trait === "D") base.D = [6, 9];
      if (g.trait === "B") base.B = [6, 9];
    });
    return base;
  }, [person, teamGap]);

  const ranges = mode === "upgrade" && upgradeRanges
    ? upgradeRanges
    : person
      ? Object.fromEntries(["A","B","C","D"].map(t => {
          const v = person[`Survey_${t}`];
          return [t, [Math.max(0, v - 1), Math.min(10, v + 1)]];
        }))
      : {};

  return (
    <div>
      <div className="hr-intro">
        Someone leaving? Generate a target profile for their replacement — either matching them, or upgrading to strengthen the team.
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 }}>
        <div className="form-group" style={{ maxWidth: "320px", flex: "1 1 280px", marginBottom: 0 }}>
          <label className="form-label">Person Leaving</label>
          <select className="form-select" value={personId} onChange={e => setPersonId(e.target.value)}>
            <option value="">Select a person...</option>
            {[...people].sort((a, b) => (a.FullName || "").localeCompare(b.FullName || "")).map(p => <option key={p.Id} value={p.Id}>{p.FullName} — {p.CompanyRole}</option>)}
          </select>
        </div>
        {person && (
          <div className="bf-mode-toggle">
            <button className={`bf-mode-btn ${mode === "match" ? "active" : ""}`} onClick={() => setMode("match")}>
              <Icon name="copy" size={14} /> Match
            </button>
            <button className={`bf-mode-btn ${mode === "upgrade" ? "active" : ""}`} onClick={() => setMode("upgrade")}>
              <Icon name="up" size={14} /> Upgrade
            </button>
          </div>
        )}
      </div>

      {!person ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="user-x" /></div>
          <div className="empty-text">Select a person to build their backfill profile.</div>
        </div>
      ) : (
        <div className="target" style={mode === "upgrade" ? { borderTopColor: "var(--green-bar)" } : {}}>
          <div className="target-head">
            <div className="target-badge"><Icon name={arch.icon} />{person.Archetype && <span>{person.Archetype}</span>}</div>
            <div>
              <div className="target-title">Replace: {person.FullName}</div>
              <div className="target-sub">{[person.JobTitle, person.CompanyRole].filter(Boolean).join(" · ")}{project ? ` · on ${project.JobName}` : ""}</div>
            </div>
            <div className="target-priority">
              <div className="target-priority-num" style={mode === "upgrade" ? { background: "var(--green-bar)" } : { background: "var(--blue)" }}>
                {mode === "upgrade" ? "Upgrade" : "Like-for-like"}
              </div>
            </div>
          </div>
          <div className="target-body">
            <div className="target-section-label">
              {mode === "upgrade" ? "Target Ranges — tuned to strengthen the team" : "Match These Trait Ranges (±1 of current)"}
            </div>
            {["A", "B", "C", "D"].map(t => {
              const [lo, hi] = ranges[t];
              const boosted = mode === "upgrade" && teamGap && teamGap.gaps.some(g => g.trait === t);
              return (
                <div className="range-row" key={t}>
                  <div className="range-label">{t} <small>{TRAIT_FULL[t]}</small></div>
                  <div>
                    <div className="range-track">
                      <div className="range-band" style={{ left: `${(lo / 10) * 100}%`, width: `${((hi - lo) / 10) * 100}%`, background: boosted ? "var(--green-bar)" : "var(--gold)" }}>{lo}–{hi}</div>
                    </div>
                    <div className="range-scale"><span>0</span><span>5</span><span>10</span></div>
                  </div>
                </div>
              );
            })}
            <div className="target-why">
              {mode === "match" ? (
                <>
                  <b>Like-for-like backfill:</b> {person.FullName} is a <b>{person.Archetype}</b> ({arch.group} group). To keep the team dynamic intact, look for a candidate in a similar trait range.
                </>
              ) : (
                <>
                  <b>Upgrade backfill:</b> Instead of just replacing {person.FullName}, this target is tuned to fix what the rest of the team still needs.
                  {teamGap && teamGap.gaps.length > 0 ? (
                    <> The crew is currently missing: {teamGap.gaps.map(g => g.label.toLowerCase()).join(", ")}. The highlighted ranges above target those gaps.</>
                  ) : (
                    <> The team is already well-balanced, so this stays close to a like-for-like match — recruit for skill and capacity.</>
                  )}
                </>
              )}
              <div className="archetypes-hint">
                <span className="arch-pill"><Icon name={arch.icon} /> {person.Archetype}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Hiring() {
  const { people: peopleRaw, projects, teamMembers, loading, error } = useData();
  const people = (peopleRaw || []).filter(p => p.IsActive !== false && p.Archetype);
  const [tab, setTab] = useState("org");

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading workforce…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Hiring & Recruiting</div>
          <div className="page-subtitle">Find the profiles to recruit as you grow</div>
        </div>
      </div>

      {people.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="users" /></div>
          <div className="empty-text">No profiled employees yet.<br />Add Culture Index profiles to analyze hiring gaps.</div>
        </div>
      ) : (
        <>
          <div className="hr-tabs">
            <div className={`hr-tab ${tab === "org" ? "active" : ""}`} onClick={() => setTab("org")}>Org-Wide Gaps</div>
            <div className={`hr-tab ${tab === "team" ? "active" : ""}`} onClick={() => setTab("team")}>Per-Team Gaps</div>
            <div className={`hr-tab ${tab === "backfill" ? "active" : ""}`} onClick={() => setTab("backfill")}>Backfill</div>
          </div>

          {tab === "org" && <OrgGaps people={people} />}
          {tab === "team" && <TeamGaps people={people} projects={projects || []} teamMembers={teamMembers || []} />}
          {tab === "backfill" && <Backfill people={people} projects={projects || []} teamMembers={teamMembers || []} />}
        </>
      )}
    </div>
  );
}
